import { Swiper } from '../vendor/swiper.js';
import { applyCardImagePalette } from './product-card.js';

const DRAG_THRESHOLD = 8;
const DESKTOP_MQ = '(min-width: 768px)';

/**
 * Homepage category tiles: native track on mobile; desktop pins two leads, Swiper on the bottom row when 9+.
 *
 * @param {ParentNode} [root=document]
 * @returns {HTMLElement[]}
 */
export function initSectionsList(root = document) {
  const instances = [];
  const elements = root.querySelectorAll('[data-sections-list], [data-sections-swiper]');
  const mql = window.matchMedia(DESKTOP_MQ);

  elements.forEach((el) => {
    if (!(el instanceof HTMLElement) || el.dataset.sectionsBound === '1') {
      return;
    }

    const track = el.querySelector(':scope .sections-list__track, :scope > .swiper-wrapper');
    if (!(track instanceof HTMLElement) || track.children.length === 0) {
      return;
    }

    el.dataset.sectionsBound = '1';
    const snapshot = el.innerHTML;
    const originalClass = el.className;
    const originalStyle = el.getAttribute('style') || '';
    const originalLayout = el.dataset.layout || 'default';
    /** @type {{ swiper: import('swiper').default | null, dragCleanup: (() => void) | null, syncHost: (() => void) | null, needsRestore: boolean }} */
    const state = { swiper: null, dragCleanup: null, syncHost: null, needsRestore: false };

    const apply = () => {
      applySectionsLayout(el, snapshot, originalClass, originalStyle, originalLayout, state, mql.matches);
    };

    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', apply);
    } else {
      mql.addListener(apply);
    }
    apply();
    instances.push(el);
  });

  return instances;
}

/**
 * @param {HTMLElement} el
 * @param {string} snapshot
 * @param {string} originalClass
 * @param {string} originalStyle
 * @param {string} originalLayout
 * @param {{ swiper: import('swiper').default | null, dragCleanup: (() => void) | null, syncHost: (() => void) | null, needsRestore: boolean }} state
 * @param {boolean} isDesktop
 */
function applySectionsLayout(el, snapshot, originalClass, originalStyle, originalLayout, state, isDesktop) {
  if (state.swiper) {
    state.swiper.destroy(true, true);
    state.swiper = null;
  }
  if (state.dragCleanup) {
    state.dragCleanup();
    state.dragCleanup = null;
  }
  if (state.syncHost) {
    window.removeEventListener('resize', state.syncHost);
    state.syncHost = null;
  }

  el.className = originalClass;
  if (originalStyle !== '') {
    el.setAttribute('style', originalStyle);
  } else {
    el.removeAttribute('style');
  }
  delete el.dataset.swiperInited;
  const oldViewport = el.querySelector(':scope > .sections-list__viewport');
  if (oldViewport instanceof HTMLElement) {
    delete oldViewport.dataset.swiperInited;
  }
  if (state.needsRestore) {
    el.innerHTML = snapshot;
  }
  state.needsRestore = true;
  el.dataset.layout = originalLayout;

  if (isDesktop) {
    flattenTrackForDesktop(el);
    if (el.querySelectorAll('.sections-list__item').length <= 8) {
      el.dataset.layout = 'default';
    }
    pinLeadTiles(el);
    tintLeadCard(el);
    if (el.dataset.layout === 'overflow') {
      state.swiper = initOverflowSwiper(el, state);
    }
    return;
  }

  state.dragCleanup = attachDragScroll(el);
}

/**
 * Match advertised product-card tint: sample the lead photo into --card-top.
 *
 * @param {HTMLElement} el
 */
function tintLeadCard(el) {
  const lead = el.querySelector('.sections-list__item--lead');
  const image = lead instanceof HTMLElement
    ? lead.querySelector('.sections-list__image')
    : null;
  if (
    !(lead instanceof HTMLElement)
    || !lead.classList.contains('sections-list__item--fill-bg')
    || !(image instanceof HTMLImageElement)
  ) {
    return;
  }
  if (lead.dataset.cardPaletteInited === '1' || lead.style.getPropertyValue('--card-top')) {
    lead.dataset.cardPaletteInited = '1';
    return;
  }
  applyCardImagePalette(lead, image, { edge: 'left' });
}

/**
 * `display: contents` + :hover on <a> drops items out of the desktop grid.
 * Lift tiles so they are the grid's real children.
 *
 * @param {HTMLElement} el
 */
function flattenTrackForDesktop(el) {
  const track = el.querySelector('.sections-list__track');
  if (!(track instanceof HTMLElement) || track.dataset.flat === '1') {
    return;
  }

  const items = [...track.querySelectorAll('.sections-list__item')];
  if (items.length === 0) {
    return;
  }

  items.forEach((item) => {
    track.appendChild(item);
  });
  track.querySelectorAll('.sections-list__group').forEach((group) => {
    group.remove();
  });
  track.dataset.flat = '1';
}

/**
 * Keep the two lead tiles in a static top row; the rest stay in the track.
 *
 * @param {HTMLElement} el
 */
function pinLeadTiles(el) {
  const track = el.querySelector('.sections-list__track');
  if (!(track instanceof HTMLElement)) {
    return;
  }

  const items = [...track.querySelectorAll(':scope > .sections-list__item')];
  if (items.length < 3) {
    return;
  }

  const pinned = document.createElement('div');
  pinned.className = 'sections-list__pinned';
  pinned.append(items[0], items[1]);

  const viewport = el.querySelector(':scope > .sections-list__viewport');
  if (viewport instanceof HTMLElement) {
    el.insertBefore(pinned, viewport);
  } else {
    el.insertBefore(pinned, track);
  }
}

/**
 * @param {HTMLElement} el
 * @param {{ syncHost: (() => void) | null }} state
 * @returns {import('swiper').default | null}
 */
function initOverflowSwiper(el, state) {
  const viewport = el.querySelector(':scope > .sections-list__viewport');
  const track = el.querySelector('.sections-list__track');
  if (!(viewport instanceof HTMLElement) || !(track instanceof HTMLElement)) {
    return null;
  }

  const items = [...track.querySelectorAll(':scope > .sections-list__item')];
  if (items.length === 0) {
    return null;
  }

  items.forEach((item) => {
    item.classList.add('swiper-slide');
  });
  track.classList.add('swiper-wrapper');

  viewport.classList.add('swiper');
  viewport.dataset.swiperInited = '1';
  void viewport.offsetWidth;

  const instance = new Swiper(viewport, {
    slidesPerView: 'auto',
    slidesPerGroup: 1,
    spaceBetween: 0,
    maxBackfaceHiddenSlides: 0,
    grabCursor: true,
    simulateTouch: true,
    allowTouchMove: true,
    autoHeight: false,
    speed: 400,
    resistanceRatio: 0.65,
    watchOverflow: false,
    observer: false,
    observeParents: false,
    observeSlideChildren: false,
    resizeObserver: false,
    updateOnWindowResize: true,
  });

  instance.update();

  state.syncHost = () => {
    if (!window.matchMedia(DESKTOP_MQ).matches) {
      return;
    }
    instance.update();
  };
  window.addEventListener('resize', state.syncHost);

  return instance;
}

/**
 * Mouse drag → scrollLeft. Native overflow-x often dies under #content { overflow-x: clip }.
 * Touch stays native: pan-x scrolls the track, pan-y scrolls the page.
 *
 * @param {HTMLElement} el
 * @returns {() => void}
 */
function attachDragScroll(el) {
  let pointerId = null;
  let axis = /** @type {null | 'x' | 'y'} */ (null);
  let startX = 0;
  let startY = 0;
  let startScroll = 0;
  let dragged = false;
  let clickBlocker = null;

  const clearClickBlocker = () => {
    if (clickBlocker) {
      el.removeEventListener('click', clickBlocker, true);
      clickBlocker = null;
    }
  };

  const onPointerDown = (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) {
      return;
    }
    clearClickBlocker();
    pointerId = event.pointerId;
    axis = null;
    dragged = false;
    startX = event.clientX;
    startY = event.clientY;
    startScroll = el.scrollLeft;
  };

  const onPointerMove = (event) => {
    if (pointerId === null || event.pointerId !== pointerId) {
      return;
    }

    const dx = event.clientX - startX;
    const dy = event.clientY - startY;

    if (axis === null) {
      if (Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) {
        return;
      }
      axis = Math.abs(dx) >= Math.abs(dy) ? 'x' : 'y';
      if (axis === 'x') {
        el.setPointerCapture(event.pointerId);
      }
    }

    if (axis !== 'x') {
      return;
    }

    dragged = true;
    event.preventDefault();
    el.scrollLeft = startScroll - dx;
  };

  const onPointerUp = (event) => {
    if (pointerId === null || event.pointerId !== pointerId) {
      return;
    }
    if (dragged) {
      clearClickBlocker();
      clickBlocker = (clickEvent) => {
        clickEvent.preventDefault();
        clickEvent.stopPropagation();
        clearClickBlocker();
      };
      el.addEventListener('click', clickBlocker, true);
    }
    pointerId = null;
    axis = null;
    dragged = false;
  };

  const onPointerCancel = (event) => {
    if (pointerId === null || event.pointerId !== pointerId) {
      return;
    }
    pointerId = null;
    axis = null;
    dragged = false;
  };

  el.addEventListener('pointerdown', onPointerDown);
  el.addEventListener('pointermove', onPointerMove, { passive: false });
  el.addEventListener('pointerup', onPointerUp);
  el.addEventListener('pointercancel', onPointerCancel);

  return () => {
    clearClickBlocker();
    el.removeEventListener('pointerdown', onPointerDown);
    el.removeEventListener('pointermove', onPointerMove);
    el.removeEventListener('pointerup', onPointerUp);
    el.removeEventListener('pointercancel', onPointerCancel);
  };
}
